import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ReactComponent as AddOpportunityIcon } from '../assets/add-opportunity.svg'
import { ReactComponent as Archive } from '../assets/icon-archived.svg'
import { DeleteIcon } from '../assets/DeleteIcon.jsx'
import PageShell from '../layouts/PageShell';
import AppNavbar from '../features/AppNavbar';
import PipelineTabs from '../features/pipeline/PipelineTabs.jsx';
import Modal from '../components/Modal.jsx';
import ConfirmModal from '../components/ConfirmModal.jsx';
import ManageTagsModal from '../features/pipeline/tags/ManageTagsModal.jsx';
import { SYSTEM_TAGS } from '../features/pipeline/tags/tagsConfig.js';
import { getStageFromStatus } from "../features/pipeline/utils.js";
import { getPipelineColumns } from '../features/pipeline/grid/columns.js'
import PipelineGrid from '../features/pipeline/grid/PipelineGrid.jsx';
import { reilmAPI } from '../services/apiClient';

const Pipeline = () => {
    const navigate = useNavigate();

    const [selectedRows, setSelectedRows] = useState([])
    const [properties, setProperties] = useState([]);
    const [customTags, setCustomTags] = useState([]);
    const [activeStage, setActiveStage] = useState("all");
    const [showAddPopup, setShowAddPopup] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [showManageTags, setShowManageTags] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [deleteError, setDeleteError] = useState(null);
    const [archiveLoading, setArchiveLoading] = useState(false);
    const [loading, setLoading] = useState(true);
    const [fetchError, setFetchError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            setLoading(true);
            setFetchError(null);
            try {
                const [propertiesRes, tagsRes] = await Promise.all([
                    reilmAPI.getProperties(),
                    reilmAPI.getTags(),
                ]);
                const list = Array.isArray(propertiesRes.data?.data?.properties)
                    ? propertiesRes.data.data.properties
                    : [];
                const tags = Array.isArray(tagsRes.data?.data?.tags)
                    ? tagsRes.data.data.tags
                    : [];
                if (!cancelled) {
                    setProperties(list);
                    setCustomTags(tags);
                }
            } catch {
                if (!cancelled) setFetchError('Unable to load properties.');
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        load();
        return () => { cancelled = true; };
    }, []);

    const allTags = useMemo(() => [
        ...SYSTEM_TAGS,
        ...customTags.map(t => ({ value: t._id, label: t.name, color: t.color })),
    ], [customTags]);

    const tagCounts = useMemo(() => {
        const counts = {};
        properties.forEach(p => {
            (p.tagsAssigned ?? []).forEach(v => {
                counts[v] = (counts[v] ?? 0) + 1;
            });
        });
        return counts;
    }, [properties]);

    const mapPropertyToGridRow = (property) => {
        const primaryContact = property.contact?.[0] || {};
        const beds = property.details?.beds ?? "--";
        const baths = property.details?.baths ?? "--";
        const sqft = property.details?.sqft ?? "--";
        const yearBuilt = property.details?.yearBuilt ?? "--";
        const addressParts = [
            property.address?.street,
            property.address?.city,
            property.address?.state,
            property.address?.zip,
        ].filter(Boolean);

        return {
            id: property._id,
            addressDisplay: addressParts.join(", "),
            propertySummary: `${beds} bd • ${baths} ba • ${sqft} sqft • ${yearBuilt}`,
            score: 0,
            status: property.stage,
            stage: getStageFromStatus(property.stage),
            contactName: primaryContact.name || "--",
            contactMobile: primaryContact.mobile || "--",
            createdAtDisplay: new Date(property.createdAt).toLocaleDateString(),
            tagsAssigned: property.tagsAssigned ?? [],
            tagsDisplay: property.tagsAssigned ?? [],
            pinned: property.pin || false,
            raw: property,
        };
    }

    const filteredProperties = useMemo(() => {
        if (activeStage === 'all') return properties;
        return properties.filter(p => getStageFromStatus(p.stage) === activeStage);
    }, [properties, activeStage]);

    const handleTabSelect = (newStage) => {
        setActiveStage(newStage);
    }

    const stageCounts = useMemo(() => {
        const counts = {
            all: properties.length,
            acquisitions: 0,
            projects: 0,
            dispositions: 0,
        };

        properties?.forEach((property) => {
            const stage = getStageFromStatus(property.stage);
            if (counts[stage] !== undefined) counts[stage] += 1;
        });

        return counts;
    }, [properties]);

    const handlePropertyPopup = () => {
        setShowAddPopup(prev => !prev);
    }

    const handleConfirmModal = () => {
        setDeleteError(null);
        setShowConfirmModal(prev => !prev);
    }

    const handleDelete = async () => {
        setDeleteLoading(true);
        setDeleteError(null);
        try {
            if (selectedRows.length === 1) {
                console.log(selectedRows[0])
                await reilmAPI.deleteProperty(selectedRows[0]);
            } else {
                console.log(selectedRows)
                await reilmAPI.deletePropertiesBatch({ ids: selectedRows });
            }
            setProperties(prev => prev.filter(p => !selectedRows.includes(p._id)));
            setSelectedRows([]);
            setShowConfirmModal(false);
        } catch (err) {
            setDeleteError(err?.response?.data?.error?.message || 'Failed to delete. Please try again.');
        } finally {
            setDeleteLoading(false);
        }
    }

    const handleArchive = async () => {
        if (!selectedRows.length) return;
        setArchiveLoading(true);
        try {
            await reilmAPI.archivePropertiesBatch({ ids: selectedRows });
            setProperties(prev => prev.filter(p => !selectedRows.includes(p._id)));
            setSelectedRows([]);
        } catch {
            // silent — can add error state later
        } finally {
            setArchiveLoading(false);
        }
    }

    const handleStageUpdate = (propertyId, newStage) => {
        setProperties(prev =>
            prev.map(p => p._id === propertyId ? { ...p, stage: newStage } : p)
        );
    };

    const handleTagsUpdate = (propertyId, tagsAssigned) => {
        setProperties(prev =>
            prev.map(p => p._id === propertyId ? { ...p, tagsAssigned } : p)
        );
    };

    const handlePinUpdate = (propertyId, pinValue) => {
        setProperties(prev =>
            prev.map(p => p._id === propertyId ? { ...p, pin: pinValue } : p)
        );
    };

    const handleTagCreated = (tag) => {
        setCustomTags(prev => [...prev, tag]);
    };

    const handleTagUpdated = (tag) => {
        setCustomTags(prev => prev.map(t => t._id === tag._id ? tag : t));
    };

    const handleTagDeleted = (tagId) => {
        setCustomTags(prev => prev.filter(t => t._id !== tagId));
        setProperties(prev =>
            prev.map(p => ({
                ...p,
                tagsAssigned: (p.tagsAssigned ?? []).filter(v => v !== tagId),
            }))
        );
    };

    const columns = useMemo(
        () => getPipelineColumns({
            activeStage,
            onStageUpdate: handleStageUpdate,
            allTags,
            onTagsUpdate: handleTagsUpdate,
            onManageTags: () => setShowManageTags(true),
            onNavigate: (id) => navigate(`/property/${id}`),
            onPinUpdate: handlePinUpdate,
        }),
        [activeStage, allTags] // eslint-disable-line react-hooks/exhaustive-deps
    );

    const rows = [...filteredProperties]
        .sort((a, b) => (b.pin ? 1 : 0) - (a.pin ? 1 : 0))
        .map(mapPropertyToGridRow);

    return (
        <PageShell>
            <Modal 
                open={showAddPopup} 
                onClose={handlePropertyPopup} 
                save={(p) => setProperties(prev => {
                    const pinned   = prev.filter(x => x.pin);
                    const unpinned = prev.filter(x => !x.pin);
                    return [...pinned, p, ...unpinned];
                })}
            />
            <ConfirmModal
                open={showConfirmModal}
                onClose={handleConfirmModal}
                onConfirm={handleDelete}
                isLoading={deleteLoading}
                error={deleteError}
            />
            <ManageTagsModal
                open={showManageTags}
                onClose={() => setShowManageTags(false)}
                customTags={customTags}
                tagCounts={tagCounts}
                onTagCreated={handleTagCreated}
                onTagUpdated={handleTagUpdated}
                onTagDeleted={handleTagDeleted}
            />
            <AppNavbar />
            <section className="mt-[30px] mb-[8px]" >
            <div className='flex justify-between pb-4'>
                <span className='flex gap-x-[8px]'>
                    <button
                        className='flex h-[40px] min-w-[102px] items-center justify-center gap-x-[6px] pr-[14px] rounded-[1.2rem] border-0 bg-brand-green text-white text-[13px] outline-none hover:opacity-90 transition-opacity'
                        style={{width: '142x'}}
                        onClick={handlePropertyPopup}
                        alt='add'
                    >
                        <AddOpportunityIcon />
                        Add Property
                    </button>
                    
                    <button
                        className={`flex h-[40px] min-w-[102px] items-center justify-center gap-x-[6px] rounded-[1.2rem] border text-[13px] transition-colors ${
                            selectedRows.length > 0
                                ? 'border-brand-muted bg-white text-brand-dark'
                                : 'border-brand-muted bg-white text-brand-muted cursor-not-allowed'
                        }`}
                        onClick={selectedRows.length > 0 ? handleConfirmModal : undefined}
                        disabled={selectedRows.length === 0}
                        alt='Delete'
                    >
                        <DeleteIcon />
                        Delete
                    </button>
                </span>
                <PipelineTabs
                    activeStage={activeStage}
                    onStageChange={handleTabSelect}
                    stageCounts={stageCounts}
                />
            </div>
            </section>
            {loading && (
                <div className="flex items-center justify-center py-20 text-brand-muted text-[20px]">
                    Loading…
                </div>
            )}
            {fetchError && (
                <div className="flex items-center justify-center py-20 text-red-400 text-[20px]">
                    {fetchError}
                </div>
            )}
            {!loading && !fetchError && (
                <PipelineGrid
                    rows={rows}
                    columns={columns}
                    selectedRows={selectedRows}
                    onRowSelectionChange={setSelectedRows}
                />
            )}
        </PageShell>
    )
}

export default Pipeline
